import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Container, Card, Button, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);

  const navigate = useNavigate();

  const translations = {
    'Blog Posts': 'المقالات',
    'Latest News & Articles': 'أحدث الأخبار والمقالات',
    'Read More': 'اقرأ المزيد',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'No blog posts found': 'لم يتم العثور على مقالات',
    'Error loading blogs': 'خطأ في تحميل المقالات',
    'Loading blogs...': 'جاري تحميل المقالات...',
    'By': 'بواسطة',
    'Published on': 'نشر في',
    'Upload New Blog': 'رفع مقال جديد',
    'Firebase Configuration Error': 'خطأ في إعدادات Firebase',
    'Please check your Firebase configuration': 'يرجى التحقق من إعدادات Firebase'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if Firebase is properly initialized
        if (!db1) {
          throw new Error('Firebase is not properly initialized. Please check your configuration.');
        }
        
        console.log('Fetching blogs from Firebase...');
        
        const blogsQuery = query(
          collection(db1, 'blog_posts'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(blogsQuery);
        console.log('Found documents:', querySnapshot.size);
        
        const blogsList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          blogsList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            views: data.views || 0,
            likes: data.likes || 0
          });
        });

        setBlogs(blogsList);
        
      } catch (err) {
        console.error('Error fetching blogs:', err);
        
        // Handle specific Firebase errors
        if (err.code === 'failed-precondition') {
          setError(translateText('Please check Firebase security rules'));
        } else if (err.code === 'permission-denied') {
          setError(translateText('Access denied. Please check Firebase permissions'));
        } else if (err.message.includes('CONFIGURATION_NOT_FOUND')) {
          setError(`${translateText('Firebase Configuration Error')}: ${translateText('Please check your Firebase configuration')}`);
        } else {
          setError(`${translateText('Error loading blogs')}: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" style={{ color: '#2E8B57' }} />
        <p className="mt-3" style={{ color: '#2E8B57' }}>
          {translateText('Loading blogs...')}
        </p>
      </Container>
    );
  }

  return (
    <Container className="my-5" style={{ 
      direction: isArabic ? 'rtl' : 'ltr'
    }}>
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 style={{ color: '#2E8B57', fontWeight: '700' }}>
            {translateText('Blog Posts')}
          </h1>
          <p style={{ color: '#666' }}>
            {translateText('Latest News & Articles')}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button 
            onClick={() => navigate('/upload')}
            style={{
              backgroundColor: '#2E8B57',
              borderColor: '#2E8B57',
              borderRadius: '20px'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {translateText('Upload New Blog')}
          </Button>
          <Button 
            onClick={() => setIsArabic(!isArabic)}
            variant="outline-primary"
            style={{
              borderColor: '#2E8B57',
              color: '#2E8B57',
              borderRadius: '20px'
            }}
          >
            {isArabic ? 'English' : 'العربية'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
          <div className="mt-2">
            <small>
              <strong>Debug Info:</strong> Check browser console for detailed error information.
              Make sure your Firebase configuration is correct for production.
            </small>
          </div>
        </Alert>
      )}

      {blogs.length === 0 && !error ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '4rem', color: '#dee2e6', marginBottom: '1rem' }}>
            📝
          </div>
          <h4 style={{ color: '#666' }}>{translateText('No blog posts found')}</h4>
          <p className="text-muted mb-4">Be the first to publish a blog post!</p>
          <Button 
            onClick={() => navigate('/upload')}
            style={{
              backgroundColor: '#2E8B57',
              borderColor: '#2E8B57',
              borderRadius: '20px'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {translateText('Upload New Blog')}
          </Button>
        </div>
      ) : (
        <Row>
          {blogs.map((blog) => (
            <Col key={blog.id} lg={6} className="mb-4">
              <Card 
                className="h-100 shadow-sm" 
                style={{ 
                  border: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/blog/${blog.token}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                }}
              >
                {blog.featuredImage && (
                  <Card.Img 
                    variant="top" 
                    src={blog.featuredImage}
                    style={{ 
                      height: '200px', 
                      objectFit: 'cover',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <Card.Body className="d-flex flex-column">
                  <div className="mb-2">
                    <Badge 
                      style={{ 
                        backgroundColor: '#2E8B57', 
                        fontSize: '0.8rem'
                      }}
                    >
                      {blog.category || 'Uncategorized'}
                    </Badge>
                  </div>
                  
                  <Card.Title style={{ 
                    color: '#2E8B57',
                    fontWeight: '600',
                    lineHeight: '1.4',
                    minHeight: '68px',
                    fontSize: '1.3rem'
                  }}>
                    {blog.title || 'Untitled Post'}
                  </Card.Title>

                  {blog.excerpt && (
                    <Card.Text style={{ 
                      color: '#666',
                      flex: 1,
                      lineHeight: '1.6',
                      minHeight: '48px'
                    }}>
                      {truncateText(blog.excerpt, 120)}
                    </Card.Text>
                  )}

                  <div className="mt-auto">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small style={{ color: '#666', fontWeight: '500' }}>
                        <i className="bi bi-person me-1"></i>
                        {blog.author || 'Unknown Author'}
                      </small>
                      <small style={{ color: '#666' }}>
                        <i className="bi bi-calendar me-1"></i>
                        {formatDate(blog.createdAt)}
                      </small>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-3 text-muted">
                        <small>
                          <i className="bi bi-eye me-1"></i>
                          {blog.views || 0}
                        </small>
                        <small>
                          <i className="bi bi-hand-thumbs-up me-1"></i>
                          {blog.likes || 0}
                        </small>
                      </div>
                      <Button 
                        variant="outline-primary"
                        size="sm"
                        style={{ 
                          color: '#2E8B57', 
                          borderColor: '#2E8B57',
                          borderRadius: '20px',
                          fontWeight: '500'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/blog/${blog.token}`);
                        }}
                      >
                        {translateText('Read More')}
                        <i className="bi bi-arrow-left ms-2" style={isArabic ? {} : { transform: 'scaleX(-1)' }}></i>
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default BlogsPage;
